import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProfileStore, useProfile, useProfileStats, useProfileCourses } from '../stores/profileStore';
import { useAuth } from '../contexts/AuthContext';
import { useMessagingStore } from '../stores/messagingStore';
import {
    ProfileHeader,
    ProfileStats,
    ProfileCourses,
    FollowersList,
    BotProfileView,
} from '../components/profile';
import '../components/profile/ProfileHeader.css';
import '../components/profile/ProfileStats.css';
import '../components/profile/BotProfileView.css';
import './PublicProfilePage.css';

type TabType = 'courses' | 'followers' | 'following';

const PublicProfilePage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation('profile');
    const { user: currentUser } = useAuth();

    const [activeTab, setActiveTab] = useState<TabType>('courses');
    const [followLoading, setFollowLoading] = useState(false);
    const [chatLoading, setChatLoading] = useState(false);

    const profile = useProfile();
    const stats = useProfileStats();
    const courses = useProfileCourses();
    const {
        followers,
        following,
        loading,
        error,
        fetchProfile,
        fetchStats,
        fetchCourses,
        fetchFollowers,
        fetchFollowing,
        followUser,
        unfollowUser,
        clearProfile,
    } = useProfileStore();

    const { startConversation } = useMessagingStore();

    const isOwnProfile = currentUser?.id === userId;
    const isBot = !!profile?.isBot;

    useEffect(() => {
        if (userId) {
            fetchProfile(userId);
            fetchStats(userId);
            fetchCourses(userId);
        }
        return () => {
            clearProfile();
        };
    }, [userId, fetchProfile, fetchStats, fetchCourses, clearProfile]);

    useEffect(() => {
        if (!userId || isBot) return;

        if (activeTab === 'followers' && !followers) {
            fetchFollowers(userId);
        } else if (activeTab === 'following' && !following) {
            fetchFollowing(userId);
        }
    }, [activeTab, userId, followers, following, fetchFollowers, fetchFollowing, isBot]);

    const handleFollow = async () => {
        if (!userId || followLoading) return;
        setFollowLoading(true);
        await followUser(userId);
        setFollowLoading(false);
    };

    const handleUnfollow = async () => {
        if (!userId || followLoading) return;
        setFollowLoading(true);
        await unfollowUser(userId);
        setFollowLoading(false);
    };

    const handleMessage = async () => {
        if (!userId) return;
        try {
            const conversation = await startConversation(userId);
            if (conversation) {
                navigate(`/messages/${conversation.id}`);
            }
        } catch (error) {
            console.error('Failed to start conversation:', error);
        }
    };

    const handleStartChat = async () => {
        if (!userId || chatLoading) return;
        setChatLoading(true);
        try {
            const conversation = await startConversation(userId);
            if (conversation) {
                navigate(`/messages/${conversation.id}`);
            }
        } catch (error) {
            console.error('Failed to start chat:', error);
        } finally {
            setChatLoading(false);
        }
    };

    const handleLoadMoreCourses = () => {
        if (userId && courses) {
            fetchCourses(userId, courses.page + 1);
        }
    };

    const handleLoadMoreFollowers = () => {
        if (userId && followers) {
            fetchFollowers(userId, followers.page + 1);
        }
    };

    const handleLoadMoreFollowing = () => {
        if (userId && following) {
            fetchFollowing(userId, following.page + 1);
        }
    };

    // Loading state
    if (loading && !profile) {
        return (
            <div className="public-profile-page public-profile-page--centered">
                <div className="public-profile-page__loader">
                    <div className="public-profile-page__spinner" />
                    <p>{t('common:loading', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error && !profile) {
        return (
            <div className="public-profile-page public-profile-page--centered">
                <div className="public-profile-page__error">
                    <svg
                        width="56"
                        height="56"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <h2>{t('errors.notFound', 'Profile Not Found')}</h2>
                    <p>{error}</p>
                    <button onClick={() => navigate(-1)} className="public-profile-page__btn">
                        {t('common:goBack', 'Go Back')}
                    </button>
                </div>
            </div>
        );
    }

    if (!profile) {
        return null;
    }

    // ========================================
    // AI Bot Profile — dedicated chat-focused template
    // ========================================
    if (isBot) {
        return (
            <div className="public-profile-page">
                <div className="public-profile-page__wrap">
                    <BotProfileView
                        profile={profile}
                        onStartChat={handleStartChat}
                        chatLoading={chatLoading}
                    />
                </div>
            </div>
        );
    }

    // ========================================
    // Human Profile — social flow with courses
    // ========================================
    const tabs: { key: TabType; label: string; count?: number }[] = [
        { key: 'courses', label: t('tabs.courses', 'Courses') },
        { key: 'followers', label: t('tabs.followers', 'Followers'), count: profile.followerCount },
        { key: 'following', label: t('tabs.following', 'Following'), count: profile.followingCount },
    ];

    return (
        <div className="public-profile-page">
            <div className="public-profile-page__wrap">
                <ProfileHeader
                    profile={profile}
                    isOwnProfile={isOwnProfile}
                    onFollow={handleFollow}
                    onUnfollow={handleUnfollow}
                    onMessage={handleMessage}
                    loading={followLoading}
                />

                {stats && (
                    <div className="public-profile-page__section">
                        <ProfileStats stats={stats} isOwnProfile={isOwnProfile} />
                    </div>
                )}

                <div className="public-profile-page__section">
                    <nav className="public-profile-page__tabs" role="tablist">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                role="tab"
                                aria-selected={activeTab === tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`public-profile-page__tab ${
                                    activeTab === tab.key ? 'public-profile-page__tab--active' : ''
                                }`}
                            >
                                {tab.label}
                                {typeof tab.count === 'number' && (
                                    <span className="public-profile-page__tab-count">{tab.count}</span>
                                )}
                            </button>
                        ))}
                    </nav>

                    <div className="public-profile-page__tab-content">
                        {activeTab === 'courses' && courses && (
                            <ProfileCourses
                                courses={courses}
                                hasPublishedCourses={profile.hasPublishedCourses}
                                onLoadMore={handleLoadMoreCourses}
                                loading={loading}
                            />
                        )}

                        {activeTab === 'followers' && followers && (
                            <FollowersList
                                users={followers}
                                type="followers"
                                onLoadMore={handleLoadMoreFollowers}
                                loading={loading}
                            />
                        )}

                        {activeTab === 'following' && following && (
                            <FollowersList
                                users={following}
                                type="following"
                                onLoadMore={handleLoadMoreFollowing}
                                loading={loading}
                            />
                        )}

                        {loading && (
                            <div className="public-profile-page__tab-loader">
                                <div className="public-profile-page__spinner" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicProfilePage;
